// adminfrontend/src/admin_ui/pages/AdminPurchaseOrdersPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import adminPurchasingApi from "../services/adminPurchasingApi";
import adminInventoryApi from "../services/adminInventoryApi";
import "../styles/Admin.css";

function money(n, currency = "") {
  const v = Number(n || 0);
  if (!Number.isFinite(v)) return "-";
  return currency ? `${currency} ${v.toFixed(2)}` : v.toFixed(2);
}

function AdminPurchaseOrdersPage({ adminEmail, onLogout }) {
  // lists
  const [suppliers, setSuppliers] = useState([]);
  const [branches, setBranches] = useState([]);

  // create PO
  const [supplierId, setSupplierId] = useState("");
  const [branchId, setBranchId] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [notes, setNotes] = useState("");
  const [orderDate, setOrderDate] = useState(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10); // YYYY-MM-DD
  });
  const [items, setItems] = useState([{ productId: "", quantityOrdered: 1, unitCost: 0 }]);

  // filters for list
  const [status, setStatus] = useState("");
  const [filterSupplierId, setFilterSupplierId] = useState("");
  const [filterBranchId, setFilterBranchId] = useState("");

  // list data
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  const [limit] = useState(100);
  const [offset] = useState(0);

  const [loading, setLoading] = useState(true);
  const [mutating, setMutating] = useState(false);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");

  const normalizedRows = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);

  function calcTotal() {
    return items.reduce((sum, it) => {
      const qty = Number(it.quantityOrdered || 0);
      const cost = Number(it.unitCost || 0);
      return sum + (Number.isFinite(qty) ? qty : 0) * (Number.isFinite(cost) ? cost : 0);
    }, 0);
  }

  function setItem(idx, patch) {
    setItems((prev) =>
      prev.map((it, i) => (i === idx ? { ...it, ...patch } : it))
    );
  }

  function addItem() {
    setItems((prev) => [...prev, { productId: "", quantityOrdered: 1, unitCost: 0 }]);
  }

  function removeItem(idx) {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  }

  async function loadBasics() {
    const [sData, bData] = await Promise.all([
      adminPurchasingApi.listSuppliers({ limit: 500, offset: 0, status: "active" }).catch(() => null),
      adminInventoryApi.listBranches().catch(() => null),
    ]);

    const sList = (sData && sData.ok === true && (sData.suppliers || sData.items || sData.rows)) || [];
    const bList = (bData && bData.ok === true && (bData.branches || bData.items || bData.rows)) || [];

    setSuppliers(Array.isArray(sList) ? sList : []);
    setBranches(Array.isArray(bList) ? bList : []);

    if (!supplierId && Array.isArray(sList) && sList[0]) setSupplierId(String(sList[0].id));
    if (!branchId && Array.isArray(bList) && bList[0]) setBranchId(String(bList[0].id));
  }

  async function loadPOs() {
    setLoading(true);
    setError("");
    try {
      const data = await adminPurchasingApi.listPurchaseOrders({
        limit,
        offset,
        status: status || undefined,
        supplierId: filterSupplierId ? Number(filterSupplierId) : undefined,
        branchId: filterBranchId ? Number(filterBranchId) : undefined,
      });

      if (!data || data.ok !== true) throw new Error("pos_failed");

      const list = data.purchaseOrders || data.items || data.rows || [];
      setRows(Array.isArray(list) ? list : []);
      setTotal(Number(data.total || list.length || 0));
    } catch (e) {
      console.error("[AdminPurchaseOrdersPage] load error:", e);
      setError("Unable to load purchase orders. Check login or server.");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError("");
      try {
        await loadBasics();
        await loadPOs();
      } catch (e) {
        console.error("[AdminPurchaseOrdersPage] init error:", e);
        setError("Unable to load suppliers/branches or purchase orders.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onCreate(e) {
    e.preventDefault();
    setMutating(true);
    setError("");
    setOkMsg("");

    try {
      const cleanedItems = items
        .map((it) => ({
          productId: Number(String(it.productId).replace(/[^\d]/g, "")),
          quantityOrdered: Number(it.quantityOrdered),
          unitCost: Number(it.unitCost),
        }))
        .filter((it) => Number.isFinite(it.productId) && it.productId > 0 && Number(it.quantityOrdered) > 0);

      const payload = {
        supplierId: Number(supplierId),
        branchId: Number(branchId),
        orderDate: orderDate || undefined,
        currency: currency || "USD",
        notes: notes.trim() || null,
        items: cleanedItems,
      };

      const data = await adminPurchasingApi.createPurchaseOrder(payload);
      if (!data || data.ok !== true) throw new Error("create_po_failed");

      setOkMsg("Purchase order created (draft).");
      setNotes("");
      setItems([{ productId: "", quantityOrdered: 1, unitCost: 0 }]);
      await loadPOs();
    } catch (e) {
      console.error("[AdminPurchaseOrdersPage] create error:", e);
      const msg =
        e?.response?.data?.error === "bad_request"
          ? "Unable to create PO. Check required fields."
          : "Unable to create PO. Check required fields and item lines.";
      setError(msg);
    } finally {
      setMutating(false);
    }
  }

  async function doAction(fn, id) {
    setMutating(true);
    setError("");
    setOkMsg("");
    try {
      const data = await fn(id);
      if (!data || data.ok !== true) throw new Error("action_failed");
      setOkMsg("Updated.");
      await loadPOs();
    } catch (e) {
      console.error("[AdminPurchaseOrdersPage] action error:", e);
      setError("Unable to update purchase order (conflict or server error).");
    } finally {
      setMutating(false);
    }
  }

  return (
    <section className="ucx-section">
      <div className="ucx-container ucx-admin-page">
        <div className="ucx-admin-header">
          <div>
            <h1 className="ucx-admin-title">Purchase Orders</h1>
            <p className="ucx-admin-subtitle">Create POs and manage approvals/sent/cancel.</p>
          </div>

          <div className="ucx-admin-header-right">
            {adminEmail && <span className="ucx-small">Signed in as {adminEmail}</span>}
            <div className="ucx-admin-header-actions">
              <button type="button" className="ucx-btn ucx-btn-outline" onClick={loadPOs} disabled={loading}>
                {loading ? "Refreshing…" : "Refresh"}
              </button>
              <button type="button" className="ucx-admin-btn ucx-admin-btn-reject" onClick={onLogout}>
                Sign out
              </button>
            </div>
          </div>
        </div>

        <div className="ucx-admin-panel">
          <div className="ucx-admin-panel-title">Create Purchase Order</div>

          <form className="ucx-admin-form" onSubmit={onCreate}>
            <div className="ucx-admin-form-grid ucx-admin-form-grid-3">
              <div className="ucx-admin-field">
                <label>Supplier *</label>
                <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} {s.status ? `(${s.status})` : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ucx-admin-field">
                <label>Branch *</label>
                <select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name} {b.code ? `(${b.code})` : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ucx-admin-field">
                <label>Currency</label>
                <input value={currency} onChange={(e) => setCurrency(e.target.value)} placeholder="USD" />
              </div>
            </div>

            <div className="ucx-admin-form-grid ucx-admin-form-grid-3">
              <div className="ucx-admin-field">
                <label>Order date *</label>
                <input type="date" value={orderDate} onChange={(e) => setOrderDate(e.target.value)} required />
              </div>
              <div className="ucx-admin-field" style={{ gridColumn: "span 2" }}>
                <label>Notes</label>
                <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes" />
              </div>
            </div>

            <div className="ucx-admin-lineitems">
              <div className="ucx-admin-lineitems-head">
                <div className="ucx-admin-panel-title" style={{ marginBottom: 0 }}>Items</div>
                <button type="button" className="ucx-btn ucx-btn-outline" onClick={addItem}>
                  + Add item
                </button>
              </div>

              <div className="ucx-admin-table-wrapper">
                <table className="ucx-admin-table">
                  <thead>
                    <tr>
                      <th style={{ width: 140 }}>Product ID</th>
                      <th style={{ width: 140 }}>Qty</th>
                      <th style={{ width: 160 }}>Unit cost</th>
                      <th>Line total</th>
                      <th style={{ width: 90 }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it, idx) => {
                      const qty = Number(it.quantityOrdered || 0);
                      const cost = Number(it.unitCost || 0);
                      const lineTotal = (Number.isFinite(qty) ? qty : 0) * (Number.isFinite(cost) ? cost : 0);

                      return (
                        <tr key={idx}>
                          <td>
                            <input
                              className="ucx-admin-input-compact"
                              value={it.productId}
                              onChange={(e) => setItem(idx, { productId: e.target.value.replace(/[^\d]/g, "") })}
                              placeholder="e.g. 12"
                            />
                          </td>
                          <td>
                            <input
                              className="ucx-admin-input-compact"
                              type="number"
                              min="1"
                              value={it.quantityOrdered}
                              onChange={(e) => setItem(idx, { quantityOrdered: Number(e.target.value) })}
                            />
                          </td>
                          <td>
                            <input
                              className="ucx-admin-input-compact"
                              type="number"
                              min="0"
                              step="0.01"
                              value={it.unitCost}
                              onChange={(e) => setItem(idx, { unitCost: Number(e.target.value) })}
                            />
                          </td>
                          <td>{money(lineTotal, currency)}</td>
                          <td>
                            <button
                              type="button"
                              className="ucx-admin-btn ucx-admin-btn-reject"
                              onClick={() => removeItem(idx)}
                              disabled={items.length <= 1}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      );
                    })}

                    {items.length === 0 && (
                      <tr>
                        <td colSpan={5} className="ucx-admin-empty">Add at least one item.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="ucx-admin-toolbar">
                <div className="ucx-admin-toolbar-hint">
                  Estimated total: <b>{money(calcTotal(), currency)}</b>
                </div>

                <div className="ucx-admin-form-actions">
                  <button
                    type="submit"
                    className="ucx-btn ucx-btn-primary"
                    disabled={mutating || !supplierId || !branchId}
                  >
                    {mutating ? "Creating…" : "Create PO"}
                  </button>
                </div>
              </div>
            </div>

            {okMsg && <div className="ucx-admin-status-success">{okMsg}</div>}
            {error && <div className="ucx-admin-status-error">{error}</div>}
          </form>
        </div>

        <div className="ucx-admin-panel">
          <div className="ucx-admin-panel-title">Filters</div>
          <div className="ucx-admin-filters ucx-admin-filters-inventory">
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              <option value="draft">draft</option>
              <option value="approved">approved</option>
              <option value="sent">sent</option>
              <option value="cancelled">cancelled</option>
              <option value="closed">closed</option>
            </select>

            <select value={filterSupplierId} onChange={(e) => setFilterSupplierId(e.target.value)}>
              <option value="">All suppliers</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>

            <select value={filterBranchId} onChange={(e) => setFilterBranchId(e.target.value)}>
              <option value="">All branches</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>

            <button type="button" className="ucx-btn ucx-btn-primary" onClick={loadPOs} disabled={loading}>
              Apply
            </button>

            <span className="ucx-small">Showing {normalizedRows.length} of {total}</span>
          </div>
        </div>

        <div className="ucx-admin-table-wrapper">
          <table className="ucx-admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>PO #</th>
                <th>Supplier</th>
                <th>Branch</th>
                <th>Status</th>
                <th>Total</th>
                <th>Created</th>
                <th style={{ width: 280 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={8} className="ucx-admin-empty">Loading purchase orders…</td>
                </tr>
              )}

              {!loading && normalizedRows.length === 0 && (
                <tr>
                  <td colSpan={8} className="ucx-admin-empty">No purchase orders found.</td>
                </tr>
              )}

              {normalizedRows.map((po) => {
                const poStatus = (po.status || "").toLowerCase();
                const canApprove = poStatus === "draft";
                const canMarkSent = poStatus === "approved";
                const canCancel = poStatus === "draft" || poStatus === "approved" || poStatus === "sent";

                return (
                  <tr key={po.id}>
                    <td>{po.id}</td>
                    <td>{po.po_number || po.number || "-"}</td>
                    <td>{po.supplier_name || po.supplierName || po.supplier_id || "-"}</td>
                    <td>{po.branch_name || po.branchName || po.branch_id || "-"}</td>
                    <td>{po.status || "-"}</td>
                    <td>{money(po.total_amount ?? po.total ?? 0, po.currency || currency)}</td>
                    <td>{po.created_at ? new Date(po.created_at).toLocaleString() : "-"}</td>
                    <td>
                      <div className="ucx-admin-actions">
                        <button
                          type="button"
                          className="ucx-admin-btn ucx-admin-btn-approve"
                          onClick={() => doAction(adminPurchasingApi.approvePurchaseOrder, po.id)}
                          disabled={!canApprove || mutating}
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          className="ucx-btn ucx-btn-outline"
                          onClick={() => doAction(adminPurchasingApi.markSentPurchaseOrder, po.id)}
                          disabled={!canMarkSent || mutating}
                        >
                          Mark Sent
                        </button>
                        <button
                          type="button"
                          className="ucx-admin-btn ucx-admin-btn-reject"
                          onClick={() => doAction(adminPurchasingApi.cancelPurchaseOrder, po.id)}
                          disabled={!canCancel || mutating}
                        >
                          Cancel
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

export default AdminPurchaseOrdersPage;
